import React, { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import { StarIcon, ShoppingBagIcon, ArrowLeftIcon } from "@heroicons/react/24/solid"
import { StarIcon as StarOutlineIcon } from "@heroicons/react/24/outline"
import { useRecommendation } from "../contexts/RecommendationContext"

const ProductDetails = () => {
  const { productId } = useParams()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { getProductDetails, generateAffiliateLink, savePurchase, currentProducts } = useRecommendation()

  useEffect(() => {
    const findProductInRecommendations = () => {
      try {
        setLoading(true)
        setError(null)

        // First, try to find the product in the stored recommendations
        if (currentProducts && currentProducts.length > 0) {
          const foundProduct = currentProducts.find(p => p.asin === productId)

          if (foundProduct) {
            console.log("Found product in stored recommendations:", foundProduct)
            setProduct(foundProduct)
            setLoading(false)
            return
          }
        }

        // If not found in stored data, show a helpful message with a back button
        console.log("Product not found in stored recommendations")
        setError("Product not found in current recommendations.")
        setLoading(false)
      } catch (err) {
        console.error("Error finding product:", err)
        fetchProductFromAPI()
      }
    }

    const fetchProductFromAPI = async () => {
      try {
        const result = await getProductDetails(productId)

        if (result.success) {
          setProduct(result.product)
        } else {
          setError(result.error || "Failed to load product details")
        }
      } catch (err) {
        setError("Failed to load product details")
        console.error("Product details error:", err)
      } finally {
        setLoading(false)
      }
    }

    if (productId) {
      findProductInRecommendations()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, currentProducts]) // Added currentProducts to dependencies

  const handlePurchase = async () => {
    if (!product) return

    try {
      // Generate affiliate link and track purchase
      const affiliateResult = await generateAffiliateLink(product.asin, product.url || product.product_url)

      if (affiliateResult.success) {
        // Save purchase record
        await savePurchase({
          product_id: product.asin,
          product_name: product.title || product.product_title,
          product_price: product.price || product.product_price,
          amazon_url: product.url || product.product_url,
          affiliate_link: affiliateResult.affiliateLink,
        })

        // Open affiliate link
        window.open(affiliateResult.affiliateLink, "_blank", "noopener,noreferrer")
      } else {
        // Fallback to regular Amazon link
        const amazonUrl = product.url || product.product_url || `https://amazon.com/dp/${product.asin}`
        window.open(amazonUrl, "_blank", "noopener,noreferrer")
      }
    } catch (error) {
      console.error("Purchase error:", error)
      // Fallback to regular Amazon link
      const amazonUrl = product.url || product.product_url || `https://amazon.com/dp/${product.asin}`
      window.open(amazonUrl, "_blank", "noopener,noreferrer")
    }
  }

  const renderStars = rating => {
    const stars = []
    const numRating = parseFloat(rating) || 0
    const fullStars = Math.floor(numRating)
    const hasHalfStar = numRating % 1 !== 0

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<StarIcon key={i} className="h-5 w-5 text-yellow-400" />)
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} className="relative">
            <StarOutlineIcon className="h-5 w-5 text-gray-300" />
            <div className="absolute inset-0 overflow-hidden w-1/2">
              <StarIcon className="h-5 w-5 text-yellow-400" />
            </div>
          </div>,
        )
      } else {
        stars.push(<StarOutlineIcon key={i} className="h-5 w-5 text-gray-300" />)
      }
    }
    return stars
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="h-96 bg-gray-200 rounded"></div>
                <div className="space-y-4">
                  <div className="h-6 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <Link to="/recommendations" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-6">
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Recommendations
            </Link>
            <div className="text-center py-12">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
              <p className="text-gray-600 mb-4">{error || "Sorry, we couldn't find the product details you're looking for."}</p>
              <p className="text-sm text-gray-500">Product ID: {productId}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/recommendations" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-6">
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Recommendations
        </Link>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
            {/* Product Image */}
            <div className="space-y-4">
              <div className="aspect-w-1 aspect-h-1 flex justify-center items-center">
                <img
                  src={product.imageUrl || product.product_photo || product.image}
                  alt={product.title || product.product_title}
                  className="w-full h-96 object-contain rounded-lg"
                  onError={e => {
                    e.target.src = "https://picsum.photos/400/400?random=fallback"
                  }}
                />
              </div>

              {/* Additional product badges */}
              <div className="flex flex-wrap gap-2">
                {product.isPrime || product.is_prime ? (
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">Prime</span>
                ) : null}
                {product.is_amazon_choice ? (
                  <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2.5 py-0.5 rounded">Amazon's Choice</span>
                ) : null}
                {product.is_best_seller ? (
                  <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded">Best Seller</span>
                ) : null}
                {product.climate_pledge_friendly ? (
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">Climate Pledge Friendly</span>
                ) : null}
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{product.title || product.product_title}</h1>

                {/* Rating */}
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex">{renderStars(product.rating || product.product_star_rating)}</div>
                  <span className="text-sm text-gray-600">{product.rating || product.product_star_rating} out of 5</span>
                  <span className="text-sm text-gray-500">
                    ({(product.reviewCount || product.product_num_ratings || 0).toLocaleString()} reviews)
                  </span>
                </div>

                {/* Sales volume */}
                {product.sales_volume && <p className="text-sm text-gray-600 mb-4">{product.sales_volume}</p>}
              </div>

              {/* Pricing */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-baseline space-x-2 mb-2">
                  <span className="text-3xl font-bold text-gray-900">{product.price || product.product_price}</span>
                  {(product.originalPrice || product.product_original_price) &&
                    (product.originalPrice || product.product_original_price) !== (product.price || product.product_price) && (
                      <span className="text-lg text-gray-500 line-through">{product.originalPrice || product.product_original_price}</span>
                    )}
                </div>

                {product.product_minimum_offer_price && (
                  <p className="text-sm text-gray-600 mb-2">Other offers from {product.product_minimum_offer_price}</p>
                )}

                {product.delivery && <p className="text-sm text-green-600 mb-4">{product.delivery}</p>}
              </div>

              {/* Product Details */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Product Details</h3>
                <dl className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-600">ASIN:</dt>
                    <dd className="text-gray-900 font-mono">{product.asin}</dd>
                  </div>
                  {product.unit_count && (
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Unit Count:</dt>
                      <dd className="text-gray-900">{product.unit_count}</dd>
                    </div>
                  )}
                  {product.unit_price && (
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Unit Price:</dt>
                      <dd className="text-gray-900">{product.unit_price}</dd>
                    </div>
                  )}
                  {product.category && (
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Category:</dt>
                      <dd className="text-gray-900">{product.category}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Purchase Button */}
              <div className="border-t border-gray-200 pt-6">
                <button
                  onClick={handlePurchase}
                  className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <ShoppingBagIcon className="h-5 w-5" />
                  <span>Buy on Amazon</span>
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">You'll be redirected to Amazon to complete your purchase</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductDetails
